import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight, Users, Bot, Workflow, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import TrustStrip from "@/components/marketing/TrustStrip";
import { useLanguage } from "@/context/LanguageContext";

const highlightIcons = [Users, Bot, Workflow, Target];

export default function Company() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const highlights = highlightIcons.map((icon, i) => ({ icon, label: t(`about.h${i + 1}`) }));
  return (
    <div>
      <section className="relative overflow-hidden vora-hero-radial border-b border-white/8">
        <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 py-24 lg:py-28">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3.5 py-1.5">
              <Building2 className="h-3.5 w-3.5 text-[#4F8CFF]" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#8fb4ff]">{t("about.label")}</span>
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08] max-w-3xl">
              {t("about.h1a")} <span className="vora-text-gradient">{t("about.accent")}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed" data-testid="about-intro">
              {t("about.intro")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="space-y-5 text-base text-slate-300 leading-relaxed max-w-3xl" data-testid="about-story">
              <p>
                We develop intelligent, customized AI agents designed around the unique requirements of
                businesses, organizations, and individuals. From customer support and recruitment to sales,
                research, operations, and workflow automation, our AI agents are built to solve real-world
                problems and reduce repetitive work.
              </p>
              <p>
                Trusted by 1,000+ clients and AI-driven businesses worldwide, our mission is to make advanced
                AI practical, accessible, and useful for everyone.
              </p>
              <p>
                We work closely with every client to understand their challenges, design the right AI solution,
                integrate it into their existing workflow, and continuously improve its performance.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-12 vora-card rounded-2xl p-7 sm:p-9 relative overflow-hidden" data-testid="founder-note">
              <span className="absolute -top-4 -left-1 text-[120px] leading-none text-[#4F8CFF]/10 font-serif select-none" aria-hidden>&ldquo;</span>
              <SectionLabel>{t("about.founderLabel")}</SectionLabel>
              <div className="mt-5 space-y-4 text-base text-slate-200 leading-relaxed italic">
                <p>
                  We started Vvvoraa with one simple belief: AI should make life easier, not more complicated.
                </p>
                <p>
                  Every business, big or small, has a dream behind it. We want to help that dream grow with
                  technology that is simple, useful, and made for real people.
                </p>
                <p>
                  We are a growing team, and we reply to every message personally. Behind every email is a real
                  person who cares. Tell us what you need, and we will find the right solution together.
                </p>
              </div>
              <p className="mt-6 text-sm font-semibold text-white">tds <span className="text-slate-400 font-normal">· Founder</span></p>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {highlights.map((h, i) => (
              <Reveal key={h.label} delay={(i % 4) * 0.06}>
                <div className="h-full vora-card vora-card-hover rounded-2xl p-5 text-center" data-testid={`about-highlight-${i + 1}`}>
                  <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[#4F8CFF]/12 text-[#4F8CFF]">
                    <h.icon className="h-5 w-5" />
                  </span>
                  <p className="mt-4 text-sm font-semibold text-white">{h.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 vora-card rounded-2xl p-7" data-testid="about-mission">
              <SectionLabel>{t("about.missionLabel")}</SectionLabel>
              <p className="mt-4 text-lg text-white leading-relaxed font-medium max-w-3xl">
                {t("about.mission")}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.11}>
            <div className="mt-8 vora-card rounded-2xl p-7" data-testid="about-team">
              <SectionLabel>{t("about.teamLabel")}</SectionLabel>
              <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-3xl">
                Vvvoraa is a remote-first team based in Singapore, with people working across Germany, India,
                and Dubai. Different countries, one purpose: to make AI simple and useful for every business.
              </p>
              <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-3xl">
                We believe great technology should feel human. That is why we listen first, then build.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Singapore", "Germany", "India", "Dubai"].map((loc) => (
                  <span key={loc} className="text-xs rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3 py-1.5 text-slate-200">{loc}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-10 text-center">
              <p className="text-xl font-semibold text-white">{t("about.ctaLine")}</p>
              <Button
                onClick={() => navigate("/contact")}
                className="mt-5 h-12 px-7 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold group"
                data-testid="about-cta-contact"
              >
                {t("about.ctaBtn")}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <TrustStrip showTestimonials={false} />
    </div>
  );
}
